import {
  TRACK_ERROR_STATUS,
  TrackStatus,
  useHasMediaDevice,
} from "@/utils/helpers/livekit/hasMediaDevice";
import { LocalAudioTrack, LocalVideoTrack } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function useMediaTrack<T extends LocalAudioTrack | LocalVideoTrack>(
  create: () => Promise<T>,
  kind: MediaDeviceKind,
  autostart = true
) {
  const [track, setTrack] = useState<T>();
  const [status, setStatus] = useState<TrackStatus>("idle");

  const hasDevice = useHasMediaDevice(kind);
  const once = useRef(false);

  const start = useCallback(async () => {
    if (track) return track;
    if (!hasDevice) {
      setStatus("unavailable");
      return;
    }
    try {
      const t = await create();
      setTrack(t);
      setStatus("ready");
      return t;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const mapped = TRACK_ERROR_STATUS[e?.name as string];
      if (mapped) {
        setStatus(mapped);
        return;
      }
      throw e; // سایر خطاها بالا برود
    }
  }, [track, hasDevice, create]);

  const stop = useCallback(() => {
    track?.stop();
    setTrack(undefined);
    setStatus("idle");
  }, [track]);

  /* optional autostart */
  useEffect(() => {
    if (!autostart || once.current) return;
    once.current = true;
    start();
    return stop;
  }, [autostart, start, stop]);

  return useMemo(
    () => ({ track, start, stop, status }) as const,
    [track, start, stop, status]
  );
}
